import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../shared/app-configs/utilit";

export interface ComponentRawMaterialInformation {
  inputComponentRawMaterialInformationOrder: string;
  inputTcComponentRawMaterialPercentage: number | null;
  inputTcComponentRawMaterialCode: string;
}

interface RawMaterialInfoProps {
  rawMaterialData: ComponentRawMaterialInformation[]; // Data array passed as props
  setRawMaterialData: React.Dispatch<
    React.SetStateAction<ComponentRawMaterialInformation[]>
  >; // Setter function
}

// Define Yup validation schema for each row

const RawMaterialInfo: React.FC<RawMaterialInfoProps> = ({
  rawMaterialData,
  setRawMaterialData,
}) => {
  const [data, setData] =
    useState<ComponentRawMaterialInformation[]>(rawMaterialData);
  const [tableData, setTableData] =
    useState<ComponentRawMaterialInformation[]>(rawMaterialData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [rawMaterials, setRawMaterials] = useState([]);
  const { notify } = useNotification();
  const { isRestrictPopUpSave, setRestrictPopUpSave } =
    useTransactionCertificateStore();
  const validationSchema = Yup.object().shape({
    inputTcComponentRawMaterialCode: Yup.string().nullable(),
    inputTcComponentRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive")
      .max(100, "Percentage cannot exceed 100")
      .test(
        "percentage-equals-100",
        "percentage-equals-100",
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item: any, index) => {
              if (index === editingRowIndex) return sum;
              return (
                sum + parseFloat(item?.inputTcComponentRawMaterialPercentage)
              );
            }, 0);
            if (total + value > 100) {
              notify(
                "Raw Material Percentage cannot be greater than 100",
                "error"
              );
              return false;
            }
          }
          return true;
        }
      )
      .nullable(),
  });
  const { updateField } = useFormActions("input-tc-component-details");
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();

        Utills.cl(`result getProductDetails ${JSON.stringify(result)}`);

        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        Utills.cl(`rawMaterials ${JSON.stringify(rawMaterials)}`);
        setRawMaterials(rawMaterials);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };
    getRawMaterialCodes();
  }, []);
  const formik = useFormik<ComponentRawMaterialInformation>({
    initialValues: {
      inputComponentRawMaterialInformationOrder: "",
      inputTcComponentRawMaterialCode: "",
      inputTcComponentRawMaterialPercentage: 0,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };
  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcProductRawMaterialOrder = index + 1;
    });
    updateField("raw-materials-info", data);
  }, [data]);

  const handleAddNewRow = () => {
    const newRow: ComponentRawMaterialInformation = {
      inputComponentRawMaterialInformationOrder: "",
      inputTcComponentRawMaterialCode: "",
      inputTcComponentRawMaterialPercentage: null,
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);

    // Reset editing state and form
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    if (
      (formik.values.inputTcComponentRawMaterialCode == "" &&
        formik?.values?.inputTcComponentRawMaterialPercentage) ||
      (formik?.values?.inputTcComponentRawMaterialPercentage &&
        formik?.values?.inputTcComponentRawMaterialPercentage <= 0)
    ) {
      return;
    }
    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setRawMaterialData(newData); // Update parent state
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<ComponentRawMaterialInformation>[] = [
    {
      accessorKey: "inputTcComponentRawMaterialCode",
      header: "Raw Material Code",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="inputTcComponentRawMaterialCode"
          value={value}
          enableSearch={true}
          options={rawMaterials}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "inputTcComponentRawMaterialPercentage",
      header: "Percentage",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcComponentRawMaterialPercentage"
          value={value}
          inputProps={{ max: 100 }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            if (
              !newValue ||
              (Number(newValue) <= 100 && /^[0-9]*$/.test(newValue))
            ) {
              onChange(newValue);
            }
          }}
          placeholder="Enter percentage"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          border: "2px solid rgba(128, 128, 128, .2)",
          my: 4,
        }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            disabled={isRestrictPopUpSave}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
          >
            Add New Row
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialInfo;
